import { useEffect, useState } from "react";
import { AlertTriangle, Save } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Spinner } from "@/components/ui/Spinner";
import { listDeviceFootprintRates, updateDeviceFootprintRate } from "../api";
import type { DeviceFootprintRate } from "../types";

// Słownik: ile gruntu zajmuje trwale słup, złącze, stacja — per operator.
//
// Z tej liczby bierze się powierzchnia we wniosku o wyłączenie z produkcji rolniczej
// ORAZ prostokąt zakreskowany na mapie, więc każdy musi móc sprawdzić, skąd się wzięła.
// Zmienia ją tylko dyrektor techniczny; pozostali widzą wartości bez pól do edycji
// (a nie pola, po kliknięciu których dostaliby odmowę).

const OPERATOR_LABEL: Record<string, string> = {
  energa: "ENERGA-OPERATOR",
  tauron: "TAURON Dystrybucja",
  inny: "Wspólne / inny operator",
};

function RateRow({ rate, onSaved }: { rate: DeviceFootprintRate; onSaved: () => void }) {
  const [width, setWidth] = useState(String(rate.width_m));
  const [length, setLength] = useState(String(rate.length_m));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const dirty = width !== String(rate.width_m) || length !== String(rate.length_m);
  const area = (Number(width) || 0) * (Number(length) || 0);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await updateDeviceFootprintRate(rate.id, {
        width_m: Number(width),
        length_m: Number(length),
      });
      onSaved();
    } catch {
      setError("Nie udało się zapisać stawki.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr className="border-t border-[var(--color-border)]">
      <td className="py-1.5 pr-3">{rate.kind_label}</td>
      <td className="py-1.5 pr-3">
        {rate.can_edit ? (
          <Input
            value={width}
            onChange={(e) => setWidth(e.target.value)}
            className="w-20 font-mono"
            inputMode="decimal"
          />
        ) : (
          <span className="font-mono">{rate.width_m}</span>
        )}
      </td>
      <td className="py-1.5 pr-3">
        {rate.can_edit ? (
          <Input
            value={length}
            onChange={(e) => setLength(e.target.value)}
            className="w-20 font-mono"
            inputMode="decimal"
          />
        ) : (
          <span className="font-mono">{rate.length_m}</span>
        )}
      </td>
      <td className="py-1.5 pr-3 font-mono tabular-nums">{area ? area.toFixed(2) : "—"} m²</td>
      <td className="py-1.5 text-[var(--color-text-muted)]">
        {error ? <span className="text-red-600">{error}</span> : rate.note}
      </td>
      <td className="py-1.5">
        {rate.can_edit && dirty && (
          <Button size="sm" variant="secondary" loading={saving} onClick={save}>
            <Save size={12} /> Zapisz
          </Button>
        )}
      </td>
    </tr>
  );
}

export function DeviceFootprintRatesPanel() {
  const [rates, setRates] = useState<DeviceFootprintRate[] | null>(null);

  const load = () => {
    listDeviceFootprintRates()
      .then(setRates)
      .catch(() => setRates([]));
  };
  useEffect(load, []);

  if (rates === null) return <Spinner />;

  const byOperator = rates.reduce<Record<string, DeviceFootprintRate[]>>((acc, rate) => {
    (acc[rate.operator] ||= []).push(rate);
    return acc;
  }, {});
  const editable = rates.some((r) => r.can_edit);

  return (
    <div className="flex flex-col gap-4 text-xs">
      <p className="text-[var(--color-text-muted)] max-w-[70ch]">
        Z tych wymiarów liczy się powierzchnia do wyłączenia z produkcji rolniczej we wniosku
        i prostokąt zakreskowany na mapie — jedno i drugie z tej samej liczby, więc rysunek
        nie rozjedzie się z dokumentem.
        {!editable && " Zmienia je dyrektor techniczny."}
      </p>
      {Object.entries(byOperator).map(([operator, rows]) => (
        <div key={operator}>
          <div className="section-label mb-1">{OPERATOR_LABEL[operator] ?? operator}</div>
          <table className="w-full">
            <thead className="text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
              <tr>
                <th className="text-left pb-1 pr-3">Urządzenie</th>
                <th className="text-left pb-1 pr-3">Szerokość [m]</th>
                <th className="text-left pb-1 pr-3">Długość [m]</th>
                <th className="text-left pb-1 pr-3">Powierzchnia</th>
                <th className="text-left pb-1">Uwaga</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((rate) => (
                <RateRow key={rate.id} rate={rate} onSaved={load} />
              ))}
            </tbody>
          </table>
        </div>
      ))}
      {rates.length === 0 && (
        <p className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-300">
          <AlertTriangle size={12} /> Brak stawek — powierzchnie trzeba będzie wpisywać ręcznie
          przy każdym wniosku.
        </p>
      )}
    </div>
  );
}
